import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Check, ChevronsUpDown, Briefcase, Settings2, Loader2 } from 'lucide-react';
import { workspacesApi } from '../lib/api';
import type { WorkspacesInfo } from '../lib/api';
import { useAuth } from '../lib/auth';
import { switchWorkspaceAndReload } from '../lib/plans';

/** Agency-only dropdown in the Sidebar footer for switching between client workspaces. */
export default function WorkspaceSwitcher() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState<WorkspacesInfo | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    workspacesApi.list().then((res) => {
      if (res.success && res.data) setInfo(res.data);
    }).catch(() => { /* switcher just shows fewer options */ });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const currentWorkspaceId = user?.workspace?.id ?? null;
  const agencyEmail = info?.agency.email ?? user?.actor?.email ?? user?.email ?? '';
  const currentLabel = user?.workspace?.name ?? 'Agency account';

  const select = async (id: string | null) => {
    if (id === currentWorkspaceId) { setOpen(false); return; }
    setError(null);
    setSwitching(id ?? 'agency');
    const err = await switchWorkspaceAndReload(id);
    if (err) { setError(err); setSwitching(null); }
  };

  const itemClass = 'w-full flex items-center gap-2 px-3 py-2 text-left text-[12.5px] font-semibold rounded-lg transition-colors';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-2.5 py-2 rounded-[11px] bg-surface border border-line hover:border-accent transition text-left"
      >
        <span className="w-7 h-7 rounded-lg bg-accent-soft text-accent-ink flex items-center justify-center flex-shrink-0">
          {user?.workspace ? <Briefcase className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-bold text-[10px] text-ink-3 uppercase tracking-[0.1em]">Workspace</span>
          <span className="block font-semibold text-[12.5px] text-ink truncate">{currentLabel}</span>
        </span>
        <ChevronsUpDown className="w-4 h-4 text-ink-3 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-2 z-50 bg-surface border border-line rounded-[13px] shadow-card p-1.5 max-h-[360px] overflow-y-auto">
          <button onClick={() => select(null)} className={`${itemClass} ${currentWorkspaceId === null ? 'bg-accent-soft text-accent-ink' : 'text-ink-2 hover:bg-surface-2 hover:text-ink'}`}>
            <Building2 className="w-4 h-4 flex-shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block truncate">Agency account</span>
              <span className="block font-medium text-[11px] text-ink-3 truncate">{agencyEmail}</span>
            </span>
            {switching === 'agency' ? <Loader2 className="w-4 h-4 animate-spin" /> : currentWorkspaceId === null && <Check className="w-4 h-4" />}
          </button>

          {info && info.workspaces.length > 0 && (
            <div className="px-3 pt-2.5 pb-1 font-bold text-[10px] text-ink-3 uppercase tracking-[0.12em]">Client workspaces</div>
          )}
          {info?.workspaces.map((ws) => {
            const active = ws.id === currentWorkspaceId;
            return (
              <button key={ws.id} onClick={() => select(ws.id)} className={`${itemClass} ${active ? 'bg-accent-soft text-accent-ink' : 'text-ink-2 hover:bg-surface-2 hover:text-ink'}`}>
                <Briefcase className="w-4 h-4 flex-shrink-0" />
                <span className="min-w-0 flex-1 truncate">{ws.name}</span>
                {switching === ws.id ? <Loader2 className="w-4 h-4 animate-spin" /> : active && <Check className="w-4 h-4" />}
              </button>
            );
          })}

          {error && <div className="px-3 py-2 text-[12px] font-medium text-danger">{error}</div>}

          <div className="border-t border-line mt-1.5 pt-1.5">
            <Link to="/workspaces" onClick={() => setOpen(false)} className={`${itemClass} text-ink-2 hover:bg-surface-2 hover:text-ink`}>
              <Settings2 className="w-4 h-4" />
              Manage workspaces
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
